import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import CameraCapture from '../components/CameraCapture';
import RecipeEditor from './RecipeEditor';
import { IconNavCamera, IconPhoto, IconNavPlus, IconCheck, IconClose, IconWarning } from '../components/icons';
import './Upload.css';
import './demo/DemoFeatures.css';

const MAX_MULTI_FILES = 20;

function getOsPasteHint() {
  if ('ontouchstart' in window) return null;
  const ua = navigator.userAgent;
  if (/Win/.test(ua)) return 'Win + Umschalt + S → Bereich markieren → Strg+V hier einfügen';
  if (/Mac/.test(ua)) return 'Cmd + Shift + 4 → Bereich markieren → ⌘V hier einfügen';
  return 'Screenshot machen → Strg+V oder ⌘V hier einfügen';
}

/**
 * Kamera-Button: HTTPS → Custom Overlay, HTTP/Mobil → nativer capture="environment" Input.
 * iOS erlaubt kein programmatisches .click() – der User muss direkt das Label antippen.
 */
function CameraBtn({ useOverlay, onOverlayOpen, onCapture, className, label = '📷 Kamera öffnen' }) {
  const inputId = React.useId();
  if (useOverlay) {
    return <button className={className} onClick={onOverlayOpen}>{label}</button>;
  }
  return (
    <>
      <label htmlFor={inputId} className={className} style={{ cursor: 'pointer' }}>{label}</label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onCapture(f); e.target.value = ''; }}
      />
    </>
  );
}

function Upload() {
  const { user } = useAuth();
  const mode = 'multi';

  // ── Multi-Mode ────────────────────────────────────────────────
  const [multiFiles, setMultiFiles]       = useState([]);
  const [multiRunning, setMultiRunning]   = useState(false);
  const [multiDone, setMultiDone]         = useState(false);
  const [multiLastResults, setMultiLastResults] = useState([]);
  const [multiInlineId, setMultiInlineId]       = useState(null);
  const [multiActiveTab, setMultiActiveTab]     = useState(0);
  const [multiSavedTabs, setMultiSavedTabs]     = useState({});
  const multiInlineRef                          = useRef(null);
  const thumbRefs                               = useRef([]);
  const [quota, setQuota]                 = useState(null);
  const [showMultiCamera, setShowMultiCamera] = useState(false);
  const [showQuotaConfirm, setShowQuotaConfirm] = useState(false);
  const multiInputRef = useRef(null);

  // ── Effects ───────────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;
    api.getUploadQuota().then(setQuota).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Strg+V / ⌘V Screenshot direkt einfügen
  const addFilesToMultiRef = useRef(addFilesToMulti);
  useEffect(() => { addFilesToMultiRef.current = addFilesToMulti; });
  useEffect(() => {
    function handlePaste(e) {
      const tag = e.target?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of [...items]) {
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) addFilesToMultiRef.current([f]);
          break;
        }
      }
    }
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  // Thumbnail-Strip beim Tab-Wechsel mitscrollen
  useEffect(() => {
    if (!multiDone || multiLastResults.length === 0) return;
    const currentRecipe = multiLastResults[multiActiveTab];
    if (!currentRecipe) return;
    const fileIdx = multiFiles.findIndex(f => f.recipes.some(r => r.id === currentRecipe.id));
    if (fileIdx >= 0 && thumbRefs.current[fileIdx]) {
      thumbRefs.current[fileIdx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [multiActiveTab, multiDone]); // eslint-disable-line

  // ── Multi-Mode Funktionen ─────────────────────────────────────
  function addFilesToMulti(newFiles) {
    const remaining = MAX_MULTI_FILES - multiFiles.length;
    if (remaining <= 0) return;
    const toAdd = Array.from(newFiles).slice(0, remaining);
    const entries = toAdd.map(f => ({
      file: f,
      name: f.name,
      preview: URL.createObjectURL(f),
      isPublic: false,
      status: 'waiting',
      recipes: [],
      error: null,
    }));
    setMultiFiles(prev => [...prev, ...entries]);
    setMultiDone(false);
  }

  function handleMultiFileSelect(e) {
    if (e.target.files?.length) {
      addFilesToMulti(e.target.files);
      e.target.value = '';
    }
  }

  function handleMultiCameraCapture(capturedFile) {
    setShowMultiCamera(false);
    addFilesToMulti([capturedFile]);
  }

  function removeMultiFile(index) {
    setMultiFiles(prev => prev.filter((_, i) => i !== index));
  }

  function toggleMultiPublic(index) {
    setMultiFiles(prev => prev.map((f, i) => i === index ? { ...f, isPublic: !f.isPublic } : f));
  }

  function resetMulti() {
    setMultiFiles([]);
    setMultiDone(false);
    setMultiLastResults([]);
    setMultiInlineId(null);
    setMultiActiveTab(0);
    setMultiSavedTabs({});
    sessionStorage.removeItem('upload_multi_results');
    api.getUploadQuota().then(setQuota).catch(() => {});
  }

  async function handleMultiStart() {
    if (!multiFiles.length) return;
    const waitingFiles = multiFiles.filter(f => f.status === 'waiting');
    if (quota && !quota.unlimited && quota.remaining < waitingFiles.length) {
      setShowQuotaConfirm(true);
      return;
    }
    await runMultiProcess();
  }

  async function runMultiProcess() {
    setShowQuotaConfirm(false);
    if (!multiFiles.length) return;
    setMultiRunning(true);
    const allSaved = [];
    const CONCURRENCY = 5;

    const waitingIndices = multiFiles
      .map((f, i) => (f.status === 'waiting' ? i : -1))
      .filter(i => i >= 0);

    const maxAllowed = quota && !quota.unlimited ? quota.remaining : Infinity;
    const toProcess = waitingIndices.slice(0, maxAllowed);
    const toSkip = waitingIndices.slice(maxAllowed);

    if (toSkip.length > 0) {
      setMultiFiles(prev => prev.map((f, i) =>
        toSkip.includes(i) ? { ...f, status: 'skipped', error: 'Monatslimit erreicht' } : f
      ));
    }

    async function processOne(i) {
      setMultiFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'processing' } : f));
      const fileIsPublic = multiFiles[i].isPublic;
      try {
        const data = await api.uploadImage(multiFiles[i].file, '', true);
        const boxes = data.boundingBoxes || [];
        const boxResults = await Promise.all(
          boxes.map(box => api.processBox(data.filename, box, box.title, fileIsPublic))
        );
        const saved = boxResults.filter(d => d.recipe).map(d => ({ ...d.recipe, _duplicate: d.duplicate || false }));
        setMultiFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done', recipes: saved } : f));
        return saved;
      } catch (err) {
        const msg = err.message || '';
        const isLimit = msg.includes('Limit') || msg.includes('limit') || msg.includes('429');
        setMultiFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: isLimit ? 'skipped' : 'error', error: isLimit ? 'Monatslimit erreicht' : (msg || 'Fehler') } : f
        ));
        return [];
      }
    }

    for (let c = 0; c < toProcess.length; c += CONCURRENCY) {
      const chunk = toProcess.slice(c, c + CONCURRENCY);
      const results = await Promise.all(chunk.map(i => processOne(i)));
      results.forEach(saved => allSaved.push(...saved));
    }

    setMultiRunning(false);
    setMultiDone(true);
    setMultiLastResults(allSaved);
    setMultiActiveTab(0);
    setMultiSavedTabs({});
    if (allSaved.length >= 1) {
      setMultiInlineId(allSaved[0].id);
      setTimeout(() => multiInlineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
    api.getUploadQuota().then(setQuota).catch(() => {});
  }

  // ── Helpers ───────────────────────────────────────────────────
  const multiWaiting    = multiFiles.filter(f => f.status === 'waiting').length;
  const multiProcessing = multiFiles.filter(f => f.status === 'processing').length;
  const multiDoneCount  = multiFiles.filter(f => f.status === 'done').length;
  const multiErrorCount = multiFiles.filter(f => f.status === 'error' || f.status === 'skipped').length;
  const multiTotalSaved = multiFiles.reduce((sum, f) => sum + f.recipes.length, 0);
  const quotaWarn = quota && !quota.unlimited && quota.remaining < multiWaiting && multiWaiting > 0;
  const multiTotal = multiFiles.length;
  const multiProgressPct = multiTotal > 0
    ? Math.round(((multiDoneCount + multiErrorCount) / multiTotal) * 100)
    : 0;


  // ── Render ────────────────────────────────────────────────────
  if (!user) return (
    <div className="demo-feature-page" style={{ padding: '0 16px' }}>
      <div className="demo-feature-hero">
        <div className="demo-feature-icon">✍️</div>
        <h1 className="demo-feature-title">Rezept-Scanner</h1>
        <p className="demo-feature-sub">
          Foto machen — fertig. Strixly erkennt Handschrift, Druckschrift und alte Kurrentschrift.
          Zutaten, Mengen und Schritte werden automatisch strukturiert.
        </p>
      </div>

      <div className="demo-steps">
        <div className="demo-step">
          <div className="demo-step-num">1</div>
          <div className="demo-step-body">
            <div className="demo-step-label">Foto aufnehmen oder hochladen</div>
            <div className="demo-step-desc">Rezeptkarte, Kochbuchseite, Zettel vom Kühlschrank — einfach fotografieren. Auch schlechte Belichtung kein Problem.</div>
          </div>
        </div>
        <div className="demo-step">
          <div className="demo-step-num">2</div>
          <div className="demo-step-body">
            <div className="demo-step-label">KI analysiert in ~10 Sekunden</div>
            <div className="demo-step-desc">Gemini Flash erkennt die Schrift, extrahiert Zutaten mit Mengen und Einheiten, strukturiert die Zubereitungsschritte.</div>
          </div>
        </div>
        <div className="demo-step">
          <div className="demo-step-num">3</div>
          <div className="demo-step-body">
            <div className="demo-step-label">Prüfen, anpassen, fertig</div>
            <div className="demo-step-desc">Du siehst das Ergebnis sofort und kannst einzelne Felder korrigieren. Dann direkt in deiner Rezeptsammlung speichern.</div>
          </div>
        </div>
      </div>

      <div className="demo-mockup">
        <div className="demo-mockup-bar">
          <div className="demo-mockup-dot" style={{background:'#ff5f57'}}/>
          <div className="demo-mockup-dot" style={{background:'#ffbd2e'}}/>
          <div className="demo-mockup-dot" style={{background:'#28c840'}}/>
          <span style={{marginLeft:8}}>✓ Scan abgeschlossen — Omas Apfelstrudel</span>
        </div>
        <div className="demo-mockup-body">
          <div className="demo-ocr-split">
            <div>
              <div style={{fontSize:'0.72rem',fontWeight:700,color:'var(--txt3, #888)',marginBottom:8}}>📄 ORIGINAL — handgeschrieben</div>
              <div className="demo-ocr-handwrite">
                Omas Apfelstrudel<br/>
                500g Mehl, 1 Ei<br/>
                Prise Salz<br/>
                250ml lauwarm. Wasser<br/>
                6 Äpfel, Zimt, Zucker<br/>
                Butter zum Bestreichen<br/>
                <br/>
                Teig kneten, ruhen lassen.<br/>
                Äpfel schälen + schneiden.<br/>
                Ausrollen, füllen, backen.
              </div>
            </div>
            <div>
              <div style={{fontSize:'0.72rem',fontWeight:700,color:'var(--green, #2da86a)',marginBottom:8}}>✓ STRIXLY HAT ERKANNT</div>
              <div className="demo-ocr-result">
                <div className="demo-ocr-result-title">📖 Omas Apfelstrudel</div>
                {[
                  ['Mehl','500 g'],['Ei','1 Stk.'],['Salz','1 Prise'],
                  ['Wasser (lauwarm)','250 ml'],['Äpfel','6 Stk.'],
                  ['Zimt','nach Geschmack'],['Zucker','nach Geschmack'],
                  ['Butter','zum Bestreichen'],
                ].map(([name, amt]) => (
                  <div key={name} className="demo-ocr-ing">
                    <span>{name}</span>
                    <span className="demo-ocr-ing-amt">{amt}</span>
                  </div>
                ))}
                <div style={{marginTop:12,fontSize:'0.75rem',color:'var(--txt3, #888)'}}>
                  3 Zubereitungsschritte erkannt · 4 Portionen · 45 Min.
                </div>
                <div className="demo-allergen-row">
                  <span className="demo-allergen-chip warn">⚠️ Gluten</span>
                  <span className="demo-allergen-chip warn">⚠️ Eier</span>
                  <span className="demo-allergen-chip warn">⚠️ Milch</span>
                  <span className="demo-allergen-chip ok">✓ Nussfrei</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="demo-cta-box">
        <h3>Eigene Rezepte digitalisieren</h3>
        <p>Scanne deine Familienrezepte, alte Kochbücher oder handgeschriebene Zettel.<br/>Kostenlos, ohne Kreditkarte, in 30 Sekunden eingerichtet.</p>
        <a href="/login?tab=register" className="demo-cta-btn">Jetzt kostenlos starten →</a>
        <div className="demo-cta-microcopy">Keine Kreditkarte · DSGVO-konform · Server in Österreich/EU</div>
      </div>
    </div>
  );

  return (
    <div className="upload-page fade-in">
      {showMultiCamera && (
        <CameraCapture onCapture={handleMultiCameraCapture} onClose={() => setShowMultiCamera(false)} />
      )}

        {/* ── Page Header ── */}
        <div className="page-header">
          <div className="page-header-left">
            <div className="page-title">Rezept <em>scannen</em></div>
            <div className="page-sub">Foto hochladen — KI erkennt und extrahiert automatisch</div>
          </div>
        </div>


        {/* ════════ MULTI MODE ════════ */}
        {mode === 'multi' && (
          <div className="upload-multi">

            {/* Quota-Balken */}
            {quota && (
              <div className="upload-quota-bar">
                {quota.unlimited
                  ? <>✅ Unbegrenzte Scans (Pro)</>
                  : quota.remaining === 0
                    ? <>⚠️ Monatslimit erreicht. <Link to="/pakete">Jetzt upgraden →</Link></>
                    : <>📊 Noch <strong>{quota.remaining}</strong> von {quota.limit} Scans diesen Monat</>
                }
              </div>
            )}

            {/* Datei-Eingabe (versteckt) */}
            <input type="file" id="multiFileInput" style={{ display: 'none' }} ref={multiInputRef}
              onChange={handleMultiFileSelect} accept="image/*" multiple />

            {/* Vor dem Scan: Drop-Zone oder Dateiliste */}
            {!multiRunning && !multiDone && (
              <>
                {/* Thumbnail-Strip der ausgewählten Bilder */}
                {multiFiles.length > 0 && (
                  <div className="upload-thumb-strip">
                    {multiFiles.map((f, i) => (
                      <div key={i} className="upload-pre-thumb">
                        <img src={f.preview} alt="" className="upload-pre-thumb-img" />
                        <button type="button" className="upload-pre-thumb-remove" onClick={() => removeMultiFile(i)}>✕</button>
                        <button type="button" className="upload-pre-thumb-vis" onClick={() => toggleMultiPublic(i)} title={f.isPublic ? 'Öffentlich' : 'Privat'}>
                          {f.isPublic ? '🌍' : '🔒'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Kamera + Drop-Zone */}
                {multiFiles.length < MAX_MULTI_FILES ? (
                  <>
                    <div className="fridge-add-area">
                      <CameraBtn
                        useOverlay={!!(navigator.mediaDevices?.getUserMedia)}
                        onOverlayOpen={() => setShowMultiCamera(true)}
                        onCapture={f => { setShowMultiCamera(false); addFilesToMulti([f]); }}
                        className="fridge-camera-primary"
                        label={
                          <>
                            <IconNavCamera className="fridge-camera-primary-icon" size={26} />
                            <span className="fridge-camera-primary-text">
                              <strong>Foto aufnehmen</strong>
                              <small>Kamera direkt öffnen</small>
                            </span>
                          </>
                        }
                      />
                      <div className="fridge-drop-zone">
                        <label className="fridge-drop-content" htmlFor="multiFileInput">
                          <div className="fridge-drop-icon">
                            {multiFiles.length > 0 ? <IconNavPlus size={28} /> : <IconPhoto size={28} />}
                          </div>
                          <div className="fridge-drop-title">
                            {multiFiles.length > 0 ? 'Weiteres Foto hinzufügen' : 'Bilder auswählen'}
                          </div>
                          <div className="fridge-drop-hint">
                            {multiFiles.length > 0 ? 'oder Bild hier reinziehen' : `oder hier reinziehen · bis zu ${MAX_MULTI_FILES} Bilder`}
                          </div>
                          {multiFiles.length === 0 && (
                            <div className="fridge-drop-formats">JPG · PNG · WebP{'ontouchstart' in window ? '' : ' · Strg+V'}</div>
                          )}
                        </label>
                      </div>
                    </div>
                    {multiFiles.length === 0 && getOsPasteHint() && (
                      <p className="upload-ai-notice">{getOsPasteHint()}</p>
                    )}
                  </>
                ) : (
                  <p className="upload-multi-limit-hint">Maximum erreicht ({MAX_MULTI_FILES} Bilder)</p>
                )}


                {quotaWarn && (
                  <div className="upload-quota-warn">
                    ⚠ Du hast nur noch <strong>{quota.remaining}</strong> Scan{quota.remaining !== 1 ? 's' : ''} übrig,
                    aber {multiWaiting} Bilder ausgewählt. <Link to="/pakete">Upgrade auf Pro</Link>
                  </div>
                )}

                {showQuotaConfirm && (
                  <div className="upload-quota-confirm">
                    <p>Du hast nur noch <strong>{quota?.remaining}</strong> von <strong>{quota?.limit}</strong> Scans
                      übrig, willst aber <strong>{multiFiles.filter(f => f.status === 'waiting').length}</strong> Bilder
                      scannen. Es werden nur die ersten {quota?.remaining} verarbeitet.</p>
                    <div className="upload-quota-confirm-btns">
                      <button className="btn btn-primary btn-sm" onClick={runMultiProcess}>Trotzdem starten</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowQuotaConfirm(false)}>Abbrechen</button>
                    </div>
                  </div>
                )}

                {multiFiles.length > 0 && !showQuotaConfirm && (
                  <button
                    onClick={handleMultiStart}
                    className="fridge-btn-scan"
                    disabled={quota && !quota.unlimited && quota.remaining === 0}
                  >
                    {multiWaiting} {multiWaiting === 1 ? 'Bild' : 'Bilder'} scannen →
                  </button>
                )}
              </>
            )}

            {/* Während / nach Scan */}
            {(multiRunning || (multiDone && multiFiles.length > 0)) && (
              <>
                {/* Progress Bar */}
                <div className="upload-multi-progress">
                  <div className="upload-multi-progress-stats">
                    <span><span className="upload-dot upload-dot-wait"/>Wartend: {multiWaiting}</span>
                    <span><span className="upload-dot upload-dot-run"/>In Bearbeitung: {multiProcessing}</span>
                    <span><span className="upload-dot upload-dot-done"/>Gespeichert: {multiDoneCount}</span>
                    {multiErrorCount > 0 && <span><span className="upload-dot upload-dot-err"/>Fehler: {multiErrorCount}</span>}
                  </div>
                  <div className="upload-progress-track">
                    <div className="upload-progress-fill" style={{ width: `${multiProgressPct}%` }} />
                  </div>
                  <p className="upload-progress-label">{multiDoneCount + multiErrorCount} von {multiTotal} verarbeitet</p>
                </div>

                {/* Thumbnail Strip */}
                <div className="upload-thumb-strip">
                  {multiFiles.map((f, i) => {
                    const isActive = multiDone && multiLastResults.length > 0 &&
                      f.recipes.some(r => r.id === multiInlineId);
                    return (
                      <div
                        key={i}
                        ref={el => thumbRefs.current[i] = el}
                        className={`upload-thumb${
                          isActive ? ' upload-thumb-active-border' :
                          f.status === 'processing' ? ' upload-thumb-status-processing' :
                          f.status === 'done' ? ' upload-thumb-status-done-border' : ''
                        }${f.recipes.length > 0 ? ' upload-thumb-clickable' : ''}`}
                        onClick={() => {
                          if (f.recipes.length === 0) return;
                          const firstRecipe = f.recipes[0];
                          const tabIdx = multiLastResults.findIndex(r => r.id === firstRecipe.id);
                          if (tabIdx >= 0) {
                            setMultiActiveTab(tabIdx);
                            setMultiInlineId(firstRecipe.id);
                          }
                        }}
                      >
                        <div className="upload-thumb-img">
                          <img src={f.preview} alt="" />
                        </div>
                        <div className="upload-thumb-info">
                          <span className="upload-thumb-name">
                            {f.name.replace(/\.[^.]+$/, '').substring(0, 12)}
                          </span>
                          <span className={`upload-thumb-sts upload-thumb-sts-${f.status}`}>
                            {f.status === 'waiting'    && '○ wartend'}
                            {f.status === 'processing' && '● wird gescannt'}
                            {f.status === 'done'       && `✓ ${f.recipes.length} Rez.`}
                            {f.status === 'error'      && '✕ Fehler'}
                            {f.status === 'skipped'    && '⚠ Limit'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="upload-ai-notice">
                  🤖 Deine Bilder werden durch Vertex AI analysiert · DSGVO-konform · EU-Server · EU AI Act
                </p>

                {multiRunning && (
                  <p className="upload-running-hint">🔄 Wird verarbeitet… bitte warten</p>
                )}

                {/* Inline-Editor */}
                {multiDone && multiLastResults.length >= 1 && multiInlineId && (
                  <div className="upload-editor-wrap" ref={multiInlineRef}>
                    {multiLastResults.length > 1 && (
                      <div className="upload-multi-nav">
                        <span className="upload-multi-nav-label">
                          Rezept {multiActiveTab + 1} von {multiLastResults.length}
                        </span>
                        <div className="upload-nav-dots">
                          {multiLastResults.map((r, i) => (
                            <div
                              key={i}
                              className={`upload-nav-dot${i === multiActiveTab ? ' active' : ''}${multiSavedTabs[r.id] ? ' saved' : ''}`}
                              onClick={() => { setMultiActiveTab(i); setMultiInlineId(multiLastResults[i].id); }}
                            />
                          ))}
                        </div>
                        <div className="upload-nav-btns">
                          <button
                            className="upload-nav-btn"
                            disabled={multiActiveTab === 0}
                            onClick={() => { setMultiActiveTab(multiActiveTab - 1); setMultiInlineId(multiLastResults[multiActiveTab - 1].id); }}
                          >‹</button>
                          <button
                            className="upload-nav-btn"
                            disabled={multiActiveTab === multiLastResults.length - 1}
                            onClick={() => { setMultiActiveTab(multiActiveTab + 1); setMultiInlineId(multiLastResults[multiActiveTab + 1].id); }}
                          >›</button>
                        </div>
                      </div>
                    )}
                    <div className="upload-editor-card">
                      <div className="upload-editor-header">
                        <div className="upload-editor-num">{multiActiveTab + 1}</div>
                        <span>
                          {multiLastResults.length === 1
                            ? 'Rezept prüfen & speichern'
                            : `Rezept ${multiActiveTab + 1} von ${multiLastResults.length}`}
                        </span>
                        <Link to={`/recipe/${multiInlineId}`} className="upload-editor-link">Zur Detailseite →</Link>
                      </div>
                      <RecipeEditor
                        key={multiInlineId}
                        recipeId={String(multiInlineId)}
                        onSaved={recipe => {
                          const rid = recipe.id || multiLastResults[multiActiveTab]?.id;
                          setMultiSavedTabs(prev => ({ ...prev, [rid]: true }));
                          const next = multiLastResults.findIndex((r, i) => i !== multiActiveTab && !multiSavedTabs[r.id]);
                          if (next !== -1) { setMultiActiveTab(next); setMultiInlineId(multiLastResults[next].id); }
                        }}
                      />
                    </div>
                    {Object.keys(multiSavedTabs).length === multiLastResults.length && (
                      <div className="upload-all-saved">
                        <span><IconCheck size={14} /> Alle {multiLastResults.length} Rezepte gespeichert</span>
                        <button onClick={resetMulti} className="btn btn-ghost btn-sm">Weitere Bilder scannen</button>
                      </div>
                    )}
                  </div>
                )}


{/* Summary Bar */}
                {multiDone && (
                  <div className="upload-summary-bar">
                    <div>
                      <span className="upload-summary-count">{multiTotalSaved} Rezepte gespeichert</span>
                      <span className="upload-summary-sub">aus {multiFiles.length} Bildern</span>
                    </div>
                    <button onClick={resetMulti} className="btn btn-ghost btn-sm">
                      Neue Bilder scannen
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* FAQ */}
        {(
          <div className="upload-faq">
            <div className="sec-title">Häufige Fragen</div>
            <details className="faq-item">
              <summary>🔒 Was bedeutet „Privat"?</summary>
              <p>Das Rezept ist nur für <strong>dich</strong> sichtbar. Andere Benutzer können es weder sehen noch suchen.</p>
            </details>
            <details className="faq-item">
              <summary>🌍 Was bedeutet „Öffentlich"?</summary>
              <p>Das Rezept ist für <strong>alle angemeldeten Benutzer</strong> sichtbar. Du kannst es jederzeit nachträglich auf „Privat" ändern.</p>
            </details>
            <details className="faq-item">
              <summary>📷 Welche Bilder funktionieren am besten?</summary>
              <p>Gerade ausgerichtete, gut belichtete Fotos von Rezeptkarten oder Kochbuchseiten. Vermeide Schatten und starke Verzerrungen.</p>
            </details>
            <details className="faq-item">
              <summary>🤖 Was passiert beim Scannen?</summary>
              <p>Die KI liest den Text aus dem Bild (OCR) und erkennt automatisch Titel, Zutaten, Zubereitung, Tags, Kategorie und mögliche Allergene.</p>
            </details>
          </div>
        )}
    </div>
  );
}

export default Upload;
